import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';

import AuthorizeComponent from '../api-authorization/AuthorizeComponent';

import { NavLinks } from './NavLinks';
import { NavHeader } from './NavHeader';
import { Container } from 'reactstrap';
import { NavFooter } from './NavFooter';
import { NoAccess } from './NoAccess';
import { TemplateHelper } from '../templates/TemplateHelper';
import { ConfigurationMain } from '../configuration/ConfigurationMain';
import { Status } from './Status';

import PreferenceManager from './PreferenceManager';

import ArrayUtils from '../common/utils/ArrayUtils';
import CoreUtils from '../common/utils/CoreUtils';
import DomUtils from '../common/utils/DomUtils';
import StringUtils from '../common/utils/StringUtils';

import SessionApi from '../api-services/SessionApi';

import './Layout.css';

export class Layout extends PureComponent {
  static displayName = Layout.name;

  constructor(props) {
    super(props);

    this.currentHeight = null;
    window.addEventListener('resize', this.updateContainerHeight.bind(this));

    this.preferencesLoaded = false;

    this.session = {};
    this.counterparties = {};
    this.counterpartyLogos = {};

    this.activeContainerPath = null;
    this.useViewContainerA = true;

    this.state = {
      userName: '',
      applicationName: '',
      version: '',
      configuration: '',
      navLinks: [],
      isInitializing: true
    };
  }

  componentDidMount() {
    this.updateContainerHeight();
    this.loadSessionInfo(null);
  }

  componentWillUnmount() {
    this.clearRefreshTimeout();
  }

  updateContainerHeight() {
    if (window.innerHeight === this.currentHeight)
      return;

    this.refreshTimeout = null;
    this.currentHeight = window.innerHeight;

    var headerElement = DomUtils.getElementItemByClassName('pwx-main-header');
    var containerElement = DomUtils.getElementItemByClassName('pwx-main-container');
    var footerElement = DomUtils.getElementItemByClassName('pwx-main-footer');

    var containerHeight = this.currentHeight - headerElement.offsetHeight - footerElement.offsetHeight;
    DomUtils.setElementHeight(containerElement, containerHeight);
  }

  async loadSessionInfo() {
    this.refreshTimeout = null;

    var sessionInfoDto = await SessionApi.getSessionInfo();

    this.session = {
      userName: sessionInfoDto.userName,
      isPowerexUser: sessionInfoDto.isPowerexUser,
      locale: sessionInfoDto.locale,
      timeZoneShortName: sessionInfoDto.timeZoneShortName,
      timeZoneLongName: sessionInfoDto.timeZoneLongName
    };

    var navLinks = [];

    if (!sessionInfoDto.isLoggedIn) {
      if (this.preferencesLoaded) {
        PreferenceManager.clearUserPreferences();
        this.preferencesLoaded = false;
      }

      navLinks.push(NavLinks.loginNavLink);
      window.location.replace(`${window.location.origin}/${NavLinks.loginNavLink.pathName}`);
      return;
    } else {
      if (!this.preferencesLoaded) {
        await PreferenceManager.loadUserPreferences();
        this.preferencesLoaded = true;
      }

      await this.processCounterparties(sessionInfoDto.counterparties, navLinks);

      if (sessionInfoDto.canUserConfigure)
        navLinks.push(NavLinks.configurationNavLink);

      if (navLinks.length === 0)
        navLinks.push(NavLinks.noAccessNavLink);

      navLinks.push(NavLinks.createProfileNavLink(sessionInfoDto.userName));
      navLinks.push(NavLinks.logoutNavLink);
    }

    var navPathNames = navLinks.map(navLink => navLink.pathName);

    var nextState = {
      userName: sessionInfoDto.userName,
      applicationName: sessionInfoDto.applicationInfo.applicationName,
        applicationVersionInfo: this.resolveApplicationVersionInfo(sessionInfoDto.applicationInfo.version, sessionInfoDto.applicationInfo.configuration),
      navLinks: navLinks,
      isInitializing: false
    };

    this.updateStateIfNeeded(nextState);

    if (navLinks.length > 0 && navPathNames.indexOf(this.props.location.pathname) === -1)
      this.props.history.replace(navLinks[0].pathName);

    this.refreshTimeout = setTimeout(this.loadSessionInfo.bind(this), sessionInfoDto.accessRetryIntervalMs);
  }

  clearRefreshTimeout() {
    if (this.refreshTimeout === null)
      return;

    clearTimeout(this.refreshTimeout);
    this.refreshTimeout = null;
  }

  updateStateIfNeeded(nextState) {
    var propertyNames = CoreUtils.getObjectPropertyNames(nextState);

    var currentState = {};
    propertyNames.forEach(
      propertyName => currentState[propertyName] = this.state[propertyName]
    );

    if (CoreUtils.areValuesEquivalent(nextState, currentState))
      return;

    this.setState(nextState);
  }

  resolveApplicationVersionInfo(version, configuration) {
    var versionInfo = '';

    if (version !== null && version !== '')
      versionInfo += 'Version ' + version;

    if (configuration !== null && configuration !== '')
      versionInfo += ' (' + configuration + ')';

    return versionInfo;
  }

  async processCounterparties(counterpartyDtos, navLinks) {
    var remainingShortNames = CoreUtils.getObjectPropertyNames(this.counterparties);

    for (var index = 0; index < counterpartyDtos.length; index++) {
      var counterpartyDto = counterpartyDtos[index];

      if (this.counterpartyLogos[counterpartyDto.shortName] === undefined) {
        var counterpartyLogoDto = await SessionApi.getCounterpartyLogo(counterpartyDto.code);

        if (StringUtils.isNonEmptyString(counterpartyLogoDto.imageDataUri)) {
          this.counterpartyLogos[counterpartyDto.shortName] = counterpartyLogoDto.imageDataUri;
        }
      }

      if (this.counterparties[counterpartyDto.shortName] === undefined)
        this.counterparties[counterpartyDto.shortName] = counterpartyDto;
      else
        Object.assign(this.counterparties[counterpartyDto.shortName], counterpartyDto)

      ArrayUtils.removeElement(remainingShortNames, counterpartyDto.shortName);

      navLinks.push({
        key: counterpartyDto.shortName.toLowerCase(),
        displayName: counterpartyDto.displayName,
        pathName: `/${counterpartyDto.shortName}`,
        logoImage: this.counterpartyLogos[counterpartyDto.shortName],
        visible: true
      });
    }

    remainingShortNames.forEach(remainingShortName => {
      delete this.counterparties[remainingShortName];
      delete this.counterpartyLogos[remainingShortName];
    });
  }

  render () {
    return (
      <div>
        <NavHeader navLinks={this.state.navLinks} />
        <Container tabIndex="0" className="pwx-main-container" fluid>
          {this.renderContent(this.state.isInitializing)}
        </Container>
        <NavFooter
          userName={this.state.userName}
          applicationName={this.state.applicationName}
          applicationVersionInfo={this.state.applicationVersionInfo}
        />
      </div>
    );
  }

  renderContent(isInitializing) {
    if (isInitializing)
      return (<Status statusContent="Loading..." />);

    if (this.props.location.pathname === NavLinks.noAccessNavLink.pathName)
      return (
        <AuthorizeComponent
          location={this.props.location}
          component={NoAccess}
          session={this.session}
        />
      );

    if (this.props.location.pathname === NavLinks.configurationNavLink.pathName)
      return (
        <AuthorizeComponent
          location={this.props.location}
          component={ConfigurationMain}
          session={this.session}
        />
      );

    var pathNameKey = this.resolvePathNameKey();
    var counterparty = this.counterparties[pathNameKey];

    return CoreUtils.isDefinedAndNotNull(counterparty)
      ? this.renderViewContainer(counterparty)
      : this.props.children;
  }

  resolveViewContainer() {
    if (this.activeContainerPath === this.props.location.pathname)
      return;

    this.activeContainerPath = this.props.location.pathname;
    this.useViewContainerA = !this.useViewContainerA;
  }

  renderViewContainer(counterparty) {
    this.resolveViewContainer();

    return this.useViewContainerA
      ? (
        <ViewContainerA
          component={TemplateHelper.resolveComponent(counterparty.templateName)}
          location={this.props.location}
          session={this.session}
          counterparty={counterparty}
          displayName={counterparty.displayName}
        />
      )
      : (
        <ViewContainerB
          component={TemplateHelper.resolveComponent(counterparty.templateName)}
          location={this.props.location}
          session={this.session}
          counterparty={counterparty}
          displayName={counterparty.displayName}
        />
      );
  }

  resolvePathNameKey() {
    var pathNameKey = this.props.location.pathname;
    if (pathNameKey.startsWith('/'))
      pathNameKey = pathNameKey.substring(1);

    return pathNameKey;
  }
}

class ViewContainerA extends PureComponent {
  render() {
    const { ...rest } = this.props;
    return (<AuthorizeComponent {...rest} />);
  }
}

class ViewContainerB extends PureComponent {
  render() {
    const { ...rest } = this.props;
    return (<AuthorizeComponent {...rest} />);
  }
}

export const LayoutWithRouter = withRouter(Layout);
